// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

// Tema + density + font kullanıcı tercihi. Body data-attribute'larıyla çalışır.
// CSS index.css'te [data-theme=...] + [data-density=...] + [data-font-size=...] selectorleri var.

import {
  Building2, Feather, Layers, Rows3, Rows4, Smartphone, TabletSmartphone,
  type LucideIcon,
} from 'lucide-react';

export type ThemeId = 'sade' | 'modern' | 'profesyonel' | 'pos';
export type DensityId = 'compact' | 'comfortable' | 'mobile';
export type FontSizeId = 'small' | 'normal' | 'large';
export type FontFamilyId = 'inter' | 'roboto' | 'system';

export interface ThemePreset {
  readonly id: ThemeId;
  readonly name: string;
  readonly description: string;
  readonly icon: LucideIcon;
}

export const THEMES: readonly ThemePreset[] = [
  { id: 'sade', name: 'Sade', description: 'Beyaz/gri, büyük yazı, az animasyon', icon: Feather },
  { id: 'modern', name: 'Modern', description: 'Gradient, kart-bazlı, animasyonlu', icon: Layers },
  { id: 'profesyonel', name: 'Profesyonel', description: 'Koyu lacivert, yoğun bilgi', icon: Building2 },
  { id: 'pos', name: 'POS', description: 'Büyük dokunmatik, mobil-friendly', icon: Smartphone },
];

export const DENSITIES: readonly { id: DensityId; name: string; icon: LucideIcon }[] = [
  { id: 'compact', name: 'Kompakt', icon: Rows4 },
  { id: 'comfortable', name: 'Rahat', icon: Rows3 },
  { id: 'mobile', name: 'Mobil', icon: TabletSmartphone },
];

export const FONT_SIZES: readonly { id: FontSizeId; name: string; scale: number }[] = [
  { id: 'small', name: 'Küçük', scale: 0.92 },
  { id: 'normal', name: 'Orta', scale: 1.0 },
  { id: 'large', name: 'Büyük', scale: 1.12 },
];

export const FONT_FAMILIES: readonly { id: FontFamilyId; name: string; stack: string }[] = [
  { id: 'inter', name: 'Inter', stack: '"Inter", system-ui, -apple-system, sans-serif' },
  { id: 'roboto', name: 'Roboto', stack: '"Roboto", system-ui, sans-serif' },
  { id: 'system', name: 'Sistem', stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' },
];

const STORAGE_KEY = 'elpo:uiPrefs:v1';

export interface UiPrefs {
  theme: ThemeId;
  density: DensityId;
  fontSize: FontSizeId;
  fontFamily: FontFamilyId;
}

export const DEFAULT_PREFS: UiPrefs = {
  theme: 'modern',
  density: 'comfortable',
  fontSize: 'normal',
  fontFamily: 'inter',
};

export function loadUiPrefs(): UiPrefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<UiPrefs>;
    return {
      theme: (parsed.theme as ThemeId) ?? DEFAULT_PREFS.theme,
      density: (parsed.density as DensityId) ?? DEFAULT_PREFS.density,
      fontSize: (parsed.fontSize as FontSizeId) ?? DEFAULT_PREFS.fontSize,
      fontFamily: (parsed.fontFamily as FontFamilyId) ?? DEFAULT_PREFS.fontFamily,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function saveUiPrefs(prefs: UiPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch { /* quota dolu, sessizce geç */ }
}

export function applyUiPrefs(prefs: UiPrefs): void {
  if (typeof document === 'undefined') return;
  const body = document.body;
  body.setAttribute('data-theme', prefs.theme);
  body.setAttribute('data-density', prefs.density);
  body.setAttribute('data-font-size', prefs.fontSize);
  // Default Odoo-style form rendering (yumusak input). Opt-out icin kullanici theme degistirir.
  body.setAttribute('data-form-style', 'odoo');

  const fontStack = FONT_FAMILIES.find((f) => f.id === prefs.fontFamily)?.stack
    ?? '"Inter", system-ui, -apple-system, sans-serif';
  document.documentElement.style.setProperty('--font-family-base', fontStack);

  const fontScale = FONT_SIZES.find((s) => s.id === prefs.fontSize)?.scale ?? 1;
  document.documentElement.style.setProperty('--font-scale', String(fontScale));
}
